import { getFromStorage, saveToStorage } from './localStorage.js';
import { HUMAN_PLAYER_ID, NPC_PLAYER_ID } from '../gameLogic/constants.js';

export const STORAGE_KEY_PLAY_RECORD_ENABLED = 'historio_play_record_enabled';
export const STORAGE_KEY_PLAY_RECORD_LIMIT = 'historio_play_record_limit';
export const STORAGE_KEY_PLAY_RECORDS = 'historio_play_records';

export const DEFAULT_RECORD_LIMIT = 200;
export const MIN_RECORD_LIMIT = 1;
export const MAX_RECORD_LIMIT = 200;

/**
 * プレイ記録機能が有効かどうかを取得する（デフォルト: false）
 */
export function isPlayRecordEnabled() {
  const val = getFromStorage(STORAGE_KEY_PLAY_RECORD_ENABLED);
  return val === true;
}

/**
 * プレイ記録機能の有効/無効を設定する
 */
export function setPlayRecordEnabled(enabled) {
  saveToStorage(STORAGE_KEY_PLAY_RECORD_ENABLED, !!enabled);
}

/**
 * プレイ記録の保存上限数を取得する（1〜200、デフォルト: 200）
 */
export function getPlayRecordLimit() {
  const val = getFromStorage(STORAGE_KEY_PLAY_RECORD_LIMIT);
  const num = parseInt(val, 10);
  if (isNaN(num)) return DEFAULT_RECORD_LIMIT;
  return Math.max(MIN_RECORD_LIMIT, Math.min(MAX_RECORD_LIMIT, num));
}

/**
 * プレイ記録の保存上限数を設定する（1〜200にクランプし、上限超過時は古い記録をFIFOで削除）
 */
export function setPlayRecordLimit(limit) {
  const num = parseInt(limit, 10);
  const clamped = isNaN(num) ? DEFAULT_RECORD_LIMIT : Math.max(MIN_RECORD_LIMIT, Math.min(MAX_RECORD_LIMIT, num));
  saveToStorage(STORAGE_KEY_PLAY_RECORD_LIMIT, clamped);

  // 既存レコードが新上限を超えていれば切り詰める (FIFO)
  const records = getPlayRecords();
  if (records.length > clamped) {
    const trimmed = records.slice(records.length - clamped);
    saveToStorage(STORAGE_KEY_PLAY_RECORDS, trimmed);
  }
  return clamped;
}

/**
 * 保存されているプレイ記録の一覧を取得する
 */
export function getPlayRecords() {
  const records = getFromStorage(STORAGE_KEY_PLAY_RECORDS);
  return Array.isArray(records) ? records : [];
}

/**
 * プレイ記録を保存する（上限超過時はFIFOで最も古い記録を削除）
 */
export function savePlayRecord(record) {
  const records = getPlayRecords();
  const limit = getPlayRecordLimit();

  records.push(record);

  let updated = records;
  if (records.length > limit) {
    updated = records.slice(records.length - limit);
  }

  saveToStorage(STORAGE_KEY_PLAY_RECORDS, updated);
  return updated;
}

/**
 * プレイ記録を全削除する（軽量化）
 */
export function clearPlayRecords() {
  saveToStorage(STORAGE_KEY_PLAY_RECORDS, []);
}

/**
 * ターン進行履歴（各ターンの意識・規模）を整形し、決着ターンの最終スナップショットも含めて返す
 */
export function getFinalTurnProgression(gameState) {
  if (!gameState) return [];

  const history = [...(gameState.turnHistory || [])];
  const currentTurn = gameState.turn_number || 1;

  const finalPlayerCon = gameState.players?.[HUMAN_PLAYER_ID]?.consciousness ?? 0;
  const finalPlayerScale = gameState.players?.[HUMAN_PLAYER_ID]?.scale ?? 0;
  const finalNpcCon = gameState.players?.[NPC_PLAYER_ID]?.consciousness ?? 0;
  const finalNpcScale = gameState.players?.[NPC_PLAYER_ID]?.scale ?? 0;

  const finalPoint = {
    turnNumber: currentTurn,
    playerConsciousness: finalPlayerCon,
    playerScale: finalPlayerScale,
    opponentConsciousness: finalNpcCon,
    opponentScale: finalNpcScale,
  };

  if (history.length > 0 && history[history.length - 1].turnNumber === currentTurn) {
    history[history.length - 1] = {
      ...history[history.length - 1],
      playerConsciousness: finalPlayerCon,
      playerScale: finalPlayerScale,
      opponentConsciousness: finalNpcCon,
      opponentScale: finalNpcScale,
    };
  } else {
    history.push(finalPoint);
  }

  return history.map(item => ({
    turnNumber: item.turnNumber,
    playerConsciousness: item.playerConsciousness,
    playerScale: item.playerScale ?? 0,
    opponentConsciousness: item.npcConsciousness ?? item.opponentConsciousness ?? 0,
    opponentScale: item.npcScale ?? item.opponentScale ?? 0,
  }));
}

/**
 * 通常対戦（NPC戦）の対戦結果を抽出し、プレイ記録としてローカルストレージに保存する
 */
export function recordMatchFromGameState(gameState, appVersion) {
  if (!isPlayRecordEnabled() || !gameState) return null;

  const playerDeckInfo = gameState.initial_decks?.[HUMAN_PLAYER_ID] || {
    name: gameState.players?.[HUMAN_PLAYER_ID]?.name || 'Player Deck',
    cards: [],
  };
  const opponentDeckInfo = gameState.initial_decks?.[NPC_PLAYER_ID] || {
    name: gameState.players?.[NPC_PLAYER_ID]?.name || 'Opponent Deck',
    cards: [],
  };

  const isPlayerVictory = gameState.winner === HUMAN_PLAYER_ID;
  const playerFirst = gameState.first_player === HUMAN_PLAYER_ID;
  const turnProgression = getFinalTurnProgression(gameState);

  const matchRecord = {
    matchId: `match_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    timestamp: new Date().toISOString(),
    version: appVersion || 'unknown',
    playerFirst: playerFirst,
    playerDeck: {
      name: playerDeckInfo.name,
      cards: playerDeckInfo.cards,
    },
    opponentDeck: {
      name: opponentDeckInfo.name,
      cards: opponentDeckInfo.cards,
    },
    result: isPlayerVictory ? 'WIN' : 'LOSE',
    endReason: gameState.end_reason || (gameState.surrendered_by ? 'SURRENDER' : 'UNKNOWN'),
    totalTurns: gameState.turn_number || 1,
    turnProgression: turnProgression,
  };

  savePlayRecord(matchRecord);
  return matchRecord;
}

/**
 * プレイ記録をJSONファイルとしてダウンロード出力し、ローカルストレージから削除（自動軽量化）する
 * @returns {{ success: boolean, reason?: string, count?: number }}
 */
export function exportPlayRecordsAsJson() {
  const records = getPlayRecords();

  if (!records || records.length === 0) {
    return { success: false, reason: 'NO_DATA' };
  }

  try {
    const jsonStr = JSON.stringify(records, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const now = new Date();
    const timestampStr = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const filename = `historio_play_records_${timestampStr}.json`;

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    // 出力完了後、ローカルストレージから削除（自動軽量化）
    clearPlayRecords();

    return { success: true, count: records.length };
  } catch (err) {
    console.error('Failed to export play records:', err);
    return { success: false, reason: 'ERROR', error: err };
  }
}
